/**
 * 轻型、可复用的 SSE 管理器
 *
 * 特性：
 * - 依赖浏览器内置的 EventSource 自动重连
 * - 心跳检测（服务端若支持 ping 事件）
 * - 事件订阅/取消、销毁
 * - 连接状态监控与日志
 * - 调试接口支持
 *
 * @version 3.0.0
 * @author Frontend Team
 * @since 2025-11-19
 */

type SSEMessageHandler<T> = (event: T) => void
type RawHandler = (ev: MessageEvent) => void

/**
 * 连接状态指标
 */
export interface SSEMetrics {
  /** 连接次数 */
  connectCount: number
  /** 断线次数 */
  disconnectCount: number
  /** 接收消息数 */
  messageCount: number
  /** 最后一次连接时间 */
  lastConnectTime: number
  /** 最后一次断线时间 */
  lastDisconnectTime: number
  /** 最后一次消息时间 */
  lastMessageTime: number
  /** 当前连接状态 */
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
}

export interface SSEManagerOptions<T> {
  /** SSE 端点 URL，如 /api/tasks/:id/stream 或 /api/tasks/stream */
  url: string
  /** 是否携带凭证 */
  withCredentials?: boolean
  /** 自定义解析器（默认 JSON.parse(raw.data)） */
  parse?: (raw: MessageEvent) => T
  /** 连接打开回调 */
  onOpen?: () => void
  /** 连接关闭回调 */
  onClose?: (reason?: string) => void
  /** 错误回调 */
  onError?: (err: any) => void
  /** 心跳回调 */
  onHeartbeat?: (gap: number) => void
  /** 心跳超时（毫秒），默认 30s */
  heartbeatMs?: number
  /** 构造后立即连接，默认 true */
  immediateConnect?: boolean
  /** 是否启用调试日志，默认 false */
  debug?: boolean
}

/**
 * SSE 管理器
 *
 * 单任务或任务流均可用
 */
export class SSEManager<T = any> {
  private es: EventSource | null = null
  private handlers = new Map<string, Set<SSEMessageHandler<T>>>()
  private rawHandlers = new Map<string, Set<RawHandler>>()
  private lastMessageAt = 0
  private closed = false

  private heartbeatTimer: any = null

  // 连接状态指标
  private metrics: SSEMetrics = {
    connectCount: 0,
    disconnectCount: 0,
    messageCount: 0,
    lastConnectTime: 0,
    lastDisconnectTime: 0,
    lastMessageTime: 0,
    connectionState: 'disconnected'
  }

  constructor(private opts: SSEManagerOptions<T>) {
    if (opts.immediateConnect !== false) this.connect()

    // 注册到全局调试接口
    if (opts.debug) {
      this.registerDebugInterface()
    }
  }

  /**
   * 连接 SSE
   */
  connect() {
    if (this.closed || this.es) return

    this.clearTimers()
    this.metrics.connectionState = 'connecting'
    this.log('连接中...', { url: this.opts.url })

    const { url, withCredentials } = this.opts
    this.es = new EventSource(url, { withCredentials })

    this.es.onopen = () => {
      this.lastMessageAt = Date.now()
      this.metrics.connectCount++
      this.metrics.lastConnectTime = Date.now()
      this.metrics.connectionState = 'connected'
      this.log('连接成功', { connectCount: this.metrics.connectCount })
      this.startHeartbeat()
      this.opts.onOpen?.()
    }

    this.es.onerror = (err) => {
      this.metrics.disconnectCount++
      this.metrics.lastDisconnectTime = Date.now()

      // 区分正常关闭和错误
      // readyState: 0=CONNECTING, 1=OPEN, 2=CLOSED
      const isClosed = this.es?.readyState === 2

      if (isClosed) {
        // 正常关闭（服务器主动关闭连接）
        this.metrics.connectionState = 'disconnected'
        this.log('连接已关闭', { disconnectCount: this.metrics.disconnectCount })
      } else {
        // 连接错误
        this.metrics.connectionState = 'error'
        this.log('连接错误', { error: err, disconnectCount: this.metrics.disconnectCount })
        this.opts.onError?.(err)
      }
      // 不手动重连，依赖浏览器内置的 EventSource 自动重连
    }

    // 默认 message 事件
    this.es.onmessage = (ev) => {
      this.lastMessageAt = Date.now()
      this.metrics.messageCount++
      this.metrics.lastMessageTime = Date.now()
      // 只分发解析后的事件，不调用 dispatch 避免循环
      this.dispatchParsed('*', ev)
    }
  }

  /**
   * 注册事件监听器
   *
   * @param event 事件类型（如 progress/completed/failed/ping，或 '*' 表示所有 message 事件）
   * @param handler 事件处理器
   * @returns 取消订阅函数
   */
  on(event: string, handler: SSEMessageHandler<T>) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set())
    this.handlers.get(event)!.add(handler)

    // '*' 是特殊事件，通过 onmessage 触发，不需要绑定原生监听
    if (event === '*') {
      return () => this.off(event, handler)
    }

    // 懒绑定原生监听（每个 event 只绑定一次）
    if (!this.rawHandlers.has(event)) {
      const rawSet = new Set<RawHandler>()
      this.rawHandlers.set(event, rawSet)

      const raw = (ev: MessageEvent) => {
        this.lastMessageAt = Date.now()
        // 直接分发解析后的事件给用户处理器，不要调用 dispatch 避免循环
        this.dispatchParsed(event, ev)
      }
      rawSet.add(raw)
      this.es?.addEventListener(event, raw as any)
    }
    return () => this.off(event, handler)
  }

  /**
   * 取消事件监听器
   * 
   * @param event 事件类型
   * @param handler 事件处理器
   */
  off(event: string, handler: SSEMessageHandler<T>) {
    const set = this.handlers.get(event)
    if (!set) return
    set.delete(handler)
    if (set.size === 0) {
      // 解除原生监听
      const rawSet = this.rawHandlers.get(event)
      if (rawSet) {
        rawSet.forEach((raw) => this.es?.removeEventListener(event, raw as any))
        this.rawHandlers.delete(event)
      }
      this.handlers.delete(event)
    }
  }

  /**
   * 关闭连接
   *
   * @param reason 关闭原因
   */
  close(reason = 'manual') {
    this.closed = true
    this.metrics.connectionState = 'disconnected'
    this.log('连接关闭', { reason })
    this.clearTimers()
    if (this.es) {
      try {
        this.es.close()
      } catch { }
      this.es = null
    }
    this.opts.onClose?.(reason)
  }

  /**
   * 获取连接状态指标
   */
  getMetrics(): Readonly<SSEMetrics> {
    return { ...this.metrics }
  }

  /**
   * 重置指标
   */
  resetMetrics() {
    this.metrics = {
      connectCount: 0,
      disconnectCount: 0,
      messageCount: 0,
      lastConnectTime: 0,
      lastDisconnectTime: 0,
      lastMessageTime: 0,
      connectionState: this.metrics.connectionState
    }
    this.log('指标已重置')
  }

  /** ---- private ---- */

  /**
   * 解析消息
   */
  private parse(ev: MessageEvent): T {
    if (this.opts.parse) return this.opts.parse(ev)
    try {
      return JSON.parse(ev.data)
    } catch {
      return ev.data as unknown as T
    }
  }



  /**
   * 分发解析后的事件
   */
  private dispatchParsed(event: string, ev: MessageEvent) {
    const set = this.handlers.get(event)
    if (!set || set.size === 0) return
    const data = this.parse(ev)
    set.forEach((fn) => fn(data))
  }

  /**
   * 启动心跳检测
   */
  private startHeartbeat() {
    const hb = this.opts.heartbeatMs ?? 30_000
    this.heartbeatTimer = setInterval(() => {
      const gap = Date.now() - this.lastMessageAt

      // 触发心跳回调
      this.opts.onHeartbeat?.(gap)
      this.log('心跳检测', { gap, threshold: hb * 1.5 })

      // 如果超时，判定为连接假死，主动关闭并触发错误
      if (gap > hb * 1.5) {
        this.log('心跳超时，主动丢弃连接', { gap, threshold: hb * 1.5 })
        if (this.es) {
          try {
            this.es.close()
          } catch { }
          this.es = null
        }
        this.clearTimers()
        this.metrics.connectionState = 'error'
        this.opts.onError?.(new Error('Heartbeat timeout'))
      }
    }, hb)
  }

  /**
   * 清理定时器
   */
  private clearTimers() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  /**
   * 日志输出
   */
  private log(message: string, data?: any) {
    if (!this.opts.debug) return
    const timestamp = new Date().toISOString()
    console.log(`[SSEManager ${timestamp}] ${message}`, data || '')
  }

  /**
   * 注册全局调试接口
   */
  private registerDebugInterface() {
    if (typeof window === 'undefined') return

    if (!(window as any).__sseDebug) {
      (window as any).__sseDebug = {
        instances: [] as SSEManager<any>[],
        getAll: () => (window as any).__sseDebug.instances,
        getMetrics: () => (window as any).__sseDebug.instances.map((m: SSEManager<any>) => m.getMetrics()),
        resetAll: () => (window as any).__sseDebug.instances.forEach((m: SSEManager<any>) => m.resetMetrics())
      }
    }

    (window as any).__sseDebug.instances.push(this)
    this.log('已注册到全局调试接口 window.__sseDebug')
  }
}

/**
 * 全局 SSE 调试接口
 *
 * 使用方式：
 * - window.__sseDebug.getAll() - 获取所有 SSE 实例
 * - window.__sseDebug.getMetrics() - 获取所有实例的指标
 * - window.__sseDebug.resetAll() - 重置所有实例的指标
 */
declare global {
  interface Window {
    __sseDebug?: {
      instances: SSEManager<any>[]
      getAll: () => SSEManager<any>[]
      getMetrics: () => SSEMetrics[]
      resetAll: () => void
    }
  }
}

